import Phaser from 'phaser';
import { Palette, SceneKeys } from '@core/config/constants';
import { SCENE_MUSIC } from '@core/config/music';
import { TextButton } from '@ui/components/TextButton';
import type { PlayerProfile } from '@core/state/PlayerProfile';
import type { AudioService } from '@core/services/AudioService';
import type { DifficultyOption } from '@core/types';

/** Opacidad de la capa oscura sobre el fondo para que el texto sea legible. */
const BACKGROUND_OVERLAY_ALPHA = 0.45;

/** Ancho por debajo del cual la UI se considera "angosta" y apila elementos. */
const NARROW_WIDTH_THRESHOLD = 760;

/** Medidas del boton compacto "Volver" que se ubica arriba a la izquierda. */
const BACK_BUTTON_WIDTH = 150;
const BACK_BUTTON_HEIGHT = 56;

/** Clave bajo la cual el perfil del jugador vive en el registry global de Phaser. */
export const PLAYER_PROFILE_REGISTRY_KEY = 'playerProfile';

/** Clave bajo la cual el servicio de audio vive en el registry global de Phaser. */
export const AUDIO_REGISTRY_KEY = 'audioService';

/** Datos para renderizar la pantalla de resultados compartida. */
export interface RoundResultsOptions {
  readonly title: string;
  readonly score: number;
  readonly coins: number;
  /** Si es true, lanza confeti de celebracion (nuevo record). */
  readonly isRecord?: boolean;
}

/**
 * Escena base de la que heredan todas las escenas del juego. Centraliza utilidades
 * comunes (acceso al perfil del jugador, audio, centro de pantalla, responsividad)
 * para evitar repetir codigo en cada escena.
 */
export abstract class BaseScene extends Phaser.Scene {
  protected get profile(): PlayerProfile {
    return this.registry.get(PLAYER_PROFILE_REGISTRY_KEY) as PlayerProfile;
  }

  protected get audio(): AudioService {
    return this.registry.get(AUDIO_REGISTRY_KEY) as AudioService;
  }

  /**
   * Phaser llama init() antes de create(). Lo usamos para pedir, en cuanto la escena se
   * crea, la musica que le corresponde segun el mapa SCENE_MUSIC (si la escena no esta
   * mapeada, no cambia la musica que ya suene).
   */
  public init(): void {
    this.events.once(Phaser.Scenes.Events.CREATE, () => {
      this.audio.playSceneMusic(SCENE_MUSIC[this.sys.settings.key]);
    });
  }

  protected get centerX(): number {
    return this.scale.width / 2;
  }

  protected get centerY(): number {
    return this.scale.height / 2;
  }

  /** Indica si el lienzo esta en orientacion vertical (mas alto que ancho). */
  protected get isPortrait(): boolean {
    return this.scale.height > this.scale.width;
  }

  /** Indica si el lienzo es angosto (celular vertical), util para apilar la UI. */
  protected get isNarrow(): boolean {
    return this.scale.width < NARROW_WIDTH_THRESHOLD;
  }

  /**
   * Vincula la escena al evento de resize del Scale Manager para reconstruir el
   * layout cuando la ventana cambia de tamaño u orientacion. Llamar al inicio de
   * create() en las escenas que deben reacomodarse.
   */
  protected initResponsive(): void {
    this.scale.on(Phaser.Scale.Events.RESIZE, this.handleResize, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off(Phaser.Scale.Events.RESIZE, this.handleResize, this);
    });
  }

  private handleResize(): void {
    if (this.scene.isActive()) {
      this.scene.restart();
    }
  }

  /**
   * Dibuja una imagen de fondo que cubre toda la pantalla (sin deformarse) con una
   * capa oscura encima para mantener la legibilidad. Centraliza este patron para que
   * todas las escenas usen el mismo fondo sin repetir codigo.
   */
  protected addBackground(texture: string): void {
    const { width, height } = this.scale;
    const background = this.add.image(this.centerX, this.centerY, texture);
    const coverScale = Math.max(width / background.width, height / background.height);
    background.setScale(coverScale);

    this.add.rectangle(this.centerX, this.centerY, width, height, Palette.background, BACKGROUND_OVERLAY_ALPHA);
  }

  /**
   * Dibuja un fondo de degradado vertical (color arriba -> color abajo) que cubre toda
   * la pantalla. Util para darle ambiente propio a cada minijuego sin usar imagenes.
   */
  protected addGradientBackground(topColor: number, bottomColor: number): void {
    const { width, height } = this.scale;
    const gradient = this.add.graphics();
    gradient.fillGradientStyle(topColor, topColor, bottomColor, bottomColor, 1);
    gradient.fillRect(0, 0, width, height);
  }

  /**
   * Esparce estrellitas que titilan por toda la pantalla para dar sensacion de escenario
   * brillante. Son decorativas (no capturan toques) y se ubican detras de la UI siempre
   * que se llamen antes de crear los elementos interactivos.
   */
  protected addTwinkles(count = 28, color = 0xffffff): void {
    const { width, height } = this.scale;
    for (let i = 0; i < count; i++) {
      const x = Phaser.Math.Between(0, width);
      const y = Phaser.Math.Between(0, height);
      const outer = Phaser.Math.Between(3, 7);
      const baseAlpha = Phaser.Math.FloatBetween(0.25, 0.85);
      const star = this.add.star(x, y, 4, outer * 0.4, outer, color, baseAlpha);

      this.tweens.add({
        targets: star,
        alpha: 0.12,
        scale: 0.6,
        duration: Phaser.Math.Between(900, 2400),
        delay: Phaser.Math.Between(0, 1600),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  /**
   * Lanza una lluvia de confeti de colores que cae desde arriba y se autodestruye.
   * Decorativa (no captura toques). Se usa para celebrar un nuevo record.
   */
  protected addConfetti(count = 80): void {
    const { width, height } = this.scale;
    const colors = [Palette.primary, Palette.secondary, Palette.accent, Palette.highlight, 0xff2d8e, 0x2ecc71];

    for (let i = 0; i < count; i++) {
      const x = Phaser.Math.Between(0, width);
      const size = Phaser.Math.Between(8, 16);
      const color = colors[Phaser.Math.Between(0, colors.length - 1)];
      const piece = this.add.rectangle(x, -20, size, size * 0.6, color).setAngle(Phaser.Math.Between(0, 360));

      this.tweens.add({
        targets: piece,
        y: height + 40,
        x: x + Phaser.Math.Between(-90, 90),
        angle: piece.angle + Phaser.Math.Between(180, 540),
        duration: Phaser.Math.Between(1500, 2800),
        delay: Phaser.Math.Between(0, 450),
        ease: 'Quad.easeIn',
        onComplete: () => piece.destroy(),
      });
    }
  }

  /**
   * Aplica un baile "pseudo-3D" a un personaje 2D (una imagen plana) para darle volumen y
   * vida sin necesitar modelos 3D ni cuadros de animacion. Combina varios trucos:
   *   - rebote vertical con sombra de contacto que reacciona (sensacion de profundidad),
   *   - estirar/encoger (squash & stretch) para darle peso,
   *   - "giro" falso oscilando el ancho (parece que rota hacia los costados),
   *   - balanceo suave.
   * El contenido (imagen + accesorios) se envuelve en dos contenedores: uno interno con la
   * animacion del baile y uno externo (devuelto) para que quien lo use pueda hacerle un
   * "golpe" de escala en momentos puntuales sin romper el baile.
   */
  protected addCharacterDance(
    content: Phaser.GameObjects.GameObject | Phaser.GameObjects.GameObject[],
    opts: { x: number; y: number; size: number; intensity?: number },
  ): Phaser.GameObjects.Container {
    const intensity = opts.intensity ?? 1;
    const bob = opts.size * 0.09 * intensity;

    const shadow = this.add.ellipse(opts.x, opts.y + opts.size * 0.5, opts.size * 0.8, opts.size * 0.2, 0x000000, 0.32);

    const root = this.add.container(opts.x, opts.y);
    const inner = this.add.container(0, 0);
    inner.add(content);
    root.add(inner);

    this.tweens.add({
      targets: inner,
      y: -bob,
      duration: 520,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: shadow,
      scaleX: 0.78,
      alpha: 0.16,
      duration: 520,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: inner,
      scaleY: 1.07,
      duration: 520,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: inner,
      scaleX: { from: 1, to: 0.85 },
      duration: 1120,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    this.tweens.add({
      targets: inner,
      angle: { from: -5 * intensity, to: 5 * intensity },
      duration: 820,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    return root;
  }

  /**
   * Agrega un boton compacto "‹ Volver" en la esquina superior izquierda que lleva a la
   * escena indicada (por defecto el Hub). Toda pantalla deberia ofrecer esta salida.
   */
  protected addBackButton(target: string = SceneKeys.Hub, label = '‹ Volver'): TextButton {
    return new TextButton(this, {
      x: 20 + BACK_BUTTON_WIDTH / 2,
      y: 20 + BACK_BUTTON_HEIGHT / 2,
      width: BACK_BUTTON_WIDTH,
      height: BACK_BUTTON_HEIGHT,
      label,
      color: Palette.accent,
      onClick: () => this.scene.start(target),
    });
  }

  /**
   * Muestra un selector de dificultad sobre la escena (titulo + un boton por opcion +
   * "Volver al menú"). Al elegir, limpia el selector y llama a onPick con el indice.
   * Centraliza este patron para que cada minijuego lo reutilice.
   */
  protected showDifficultyPicker(
    title: string,
    options: readonly DifficultyOption[],
    onPick: (index: number) => void,
    highScore = 0,
  ): void {
    const { width, height } = this.scale;
    const layer: Phaser.GameObjects.GameObject[] = [];

    const overlay = this.add.rectangle(this.centerX, this.centerY, width, height, Palette.background, 0.55);
    overlay.setInteractive();
    layer.push(overlay);

    layer.push(
      this.add
        .text(this.centerX, height * 0.24, title, {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '50px',
          color: Palette.textLight,
          align: 'center',
        })
        .setOrigin(0.5),
    );

    layer.push(
      this.add
        .text(this.centerX, height * 0.24 + 52, 'Elegí la dificultad', {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '26px',
          color: '#4dd0e1',
        })
        .setOrigin(0.5),
    );

    if (highScore > 0) {
      layer.push(
        this.add
          .text(this.centerX, height * 0.24 + 90, `🏆 Tu récord: ${highScore}`, {
            fontFamily: 'Trebuchet MS, sans-serif',
            fontSize: '24px',
            color: '#ffd54f',
          })
          .setOrigin(0.5),
      );
    }

    const firstY = height * 0.45;
    options.forEach((option, index) => {
      layer.push(
        new TextButton(this, {
          x: this.centerX,
          y: firstY + index * 92,
          label: option.label,
          color: option.color,
          onClick: () => {
            layer.forEach((object) => object.destroy());
            onPick(index);
          },
        }),
      );
    });

    layer.push(
      new TextButton(this, {
        x: this.centerX,
        y: firstY + options.length * 92 + 24,
        label: 'Volver al menú',
        color: Palette.accent,
        onClick: () => this.scene.start(SceneKeys.Hub),
      }),
    );
  }

  /**
   * Pantalla de resultados compartida por los minijuegos (puntaje + estrellas y botones
   * para reintentar o volver). Acomoda los botones lado a lado o apilados segun el ancho.
   */
  protected showRoundResults(options: RoundResultsOptions): void {
    const { width, height } = this.scale;

    const overlay = this.add.rectangle(this.centerX, this.centerY, width, height, Palette.background, 0.85);
    overlay.setInteractive();

    if (options.isRecord) {
      this.addConfetti();
      this.audio.playFanfare();
    }

    this.add
      .text(this.centerX, height * 0.3, options.title, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '52px',
        color: Palette.textLight,
        align: 'center',
      })
      .setOrigin(0.5);

    this.add
      .text(this.centerX, height * 0.45, `Puntos: ${options.score}\nEstrellas ganadas: ${options.coins}`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '32px',
        color: Palette.textLight,
        align: 'center',
      })
      .setOrigin(0.5);

    const buttonsY = height * 0.66;
    if (this.isNarrow) {
      new TextButton(this, {
        x: this.centerX,
        y: buttonsY,
        label: 'Jugar de nuevo',
        color: Palette.secondary,
        onClick: () => this.scene.restart(),
      });
      new TextButton(this, {
        x: this.centerX,
        y: buttonsY + 100,
        label: 'Volver al menú',
        color: Palette.accent,
        onClick: () => this.scene.start(SceneKeys.Hub),
      });
      return;
    }

    new TextButton(this, {
      x: this.centerX - 180,
      y: buttonsY,
      label: 'Jugar de nuevo',
      color: Palette.secondary,
      onClick: () => this.scene.restart(),
    });
    new TextButton(this, {
      x: this.centerX + 180,
      y: buttonsY,
      label: 'Volver al menú',
      color: Palette.accent,
      onClick: () => this.scene.start(SceneKeys.Hub),
    });
  }
}
